import posthog from "posthog-js"
import { analyticsEventForMutation, analyticsRoute, granularTransactionEvent } from "@/lib/analytics-events"

const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim()
let enabled = Boolean(token && typeof window !== "undefined")
let activeTenantId: number | null = null
let activeTenantName: string | null = null
let activeUserId: number | null = null

if (enabled) {
  try {
    posthog.init(token!, {
      api_host: import.meta.env.VITE_POSTHOG_HOST?.trim() || "https://us.i.posthog.com",
      defaults: "2026-05-30",
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      disable_session_recording: true,
    })
  } catch {
    enabled = false
  }
}

export { posthog }

function captureEvent(name: string, properties: Record<string, string | number | undefined> = {}): void {
  if (!enabled) return
  try {
    posthog.capture(name, {
      ...properties,
      ...(activeTenantId !== null ? { tenant_id: activeTenantId } : {}),
      ...(activeTenantName ? { tenant_name: activeTenantName } : {}),
    })
  } catch {
    // Telemetry must not affect POS operations.
  }
}

export function captureApiRequest(method: string | undefined, url: string | undefined, status: number, durationMs: number): void {
  const route = analyticsRoute(url)
  if (!route) return
  captureEvent("api_request", {
    method: (method ?? "get").toUpperCase(),
    route,
    status,
    duration_ms: Math.round(durationMs),
  })
}

export function capturePosPresence(event: "pos_opened" | "pos_closed"): void {
  if (activeTenantId === null) return
  captureEvent(event)
}

export function setAnalyticsIdentity(user: { id: number; tenantId?: number | null; tenantName?: string | null } | null): void {
  if (!enabled) return
  try {
    if (!user?.tenantId) {
      if (activeUserId !== null) posthog.reset()
      activeUserId = null
      activeTenantId = null
      activeTenantName = null
      return
    }
    const tenantName = user.tenantName?.trim() || null
    const sameIdentity = activeUserId === user.id && activeTenantId === user.tenantId
    if (sameIdentity && activeTenantName === tenantName) return
    if (!sameIdentity) {
      if (activeUserId !== null) posthog.reset()
      posthog.identify(`user:${user.id}`)
    }
    posthog.group("tenant", `tenant:${user.tenantId}`, {
      tenant_id: user.tenantId,
      ...(tenantName ? { name: tenantName } : {}),
    })
    activeUserId = user.id
    activeTenantId = user.tenantId
    activeTenantName = tenantName
  } catch {
    // Authentication and workspace access must work when analytics is unavailable.
  }
}

export function captureSuccessfulMutation(
  method: string | undefined,
  url: string | undefined,
  body: unknown,
  response: unknown,
): void {
  if (!enabled || activeTenantId === null) return
  try {
    const event = analyticsEventForMutation(method, url, body, response)
    if (!event) return
    const { event: name, ...properties } = event
    captureEvent(name, properties)
    if (event.transaction_type) {
      const granular = granularTransactionEvent(event.transaction_type)
      if (granular) captureEvent(granular, properties)
    }
  } catch {
    // Analytics must never turn a completed transaction into a UI error.
  }
}

export function captureAnalyticsPageView(pathname: string): void {
  if (!enabled || activeTenantId === null) return
  try {
    posthog.capture("$pageview", {
      $current_url: `${window.location.origin}${pathname}`,
      tenant_id: activeTenantId,
      ...(activeTenantName ? { tenant_name: activeTenantName } : {}),
    })
  } catch {
    // Navigation is independent of analytics delivery.
  }
}
